import { Result } from 'better-result';
import { parseJson } from '@finnest/core/json';
import {
  InfrastructureError,
  NotFoundError,
  UnauthorizedError,
  ValidationError,
} from '@finnest/core/result';
import { OrganizationId } from '@finnest/power-admin-contracts';
import { Result as SchemaResult, Schema } from 'effect';
import { z } from 'zod';
import { finnest } from '@finnest/core/schemas/meta';
import { boundFetcherSignal, powerAdminApiPath } from '../runtime/power-admin-api.ts';
import type { OperatorHttp } from '../runtime/power-admin-api.ts';

export const OperatorCredentials = z.strictObject({
  email: z.string().min(1),
  password: z.string().min(1),
});
export type OperatorCredentials = z.infer<typeof OperatorCredentials>;
finnest.add(OperatorCredentials, {
  id: 'power-admin.operator-credentials',
  title: 'Operator login credentials',
  description: 'Email and password submitted to Better Auth sign-in.',
  version: 'v1',
});

const LoginFormPrefill = z.strictObject({
  email: z.string(),
  password: z.string(),
});
type LoginFormPrefill = z.infer<typeof LoginFormPrefill>;

const AuthMessageBody = z.object({
  message: z.string().min(1),
});
const AuthFailureBody = z.object({
  code: z.string().min(1).exactOptional(),
  message: z.string().min(1),
});
const SignInResponse = z.object({
  twoFactorRedirect: z.literal(true).exactOptional(),
});
const decodeOrganizationList = Schema.decodeResult(
  Schema.fromJsonString(Schema.Array(Schema.Struct({ id: OrganizationId }))),
);

const organizationIdsFromText = (
  text: string,
): Result<readonly OrganizationId[], ValidationError> => {
  const decoded = decodeOrganizationList(text);
  return SchemaResult.isSuccess(decoded)
    ? Result.ok(decoded.success.map((entry) => entry.id))
    : Result.err(
        new ValidationError({
          field: 'json',
          message: decoded.failure.message,
          cause: decoded.failure,
        }),
      );
};
const OperatorSessionEmail = z.object({
  user: z.object({ email: z.string().min(1) }),
});

const jsonHeaders = { accept: 'application/json', 'content-type': 'application/json' } as const;
const NETWORK_LOGIN_MESSAGE = 'Erro de rede ao entrar.';

export const loginFormPrefill: LoginFormPrefill = import.meta.env.DEV
  ? { email: 'operator@finnest.local', password: 'Operador-dev-123' }
  : { email: '', password: '' };

export const publicAuthMessage = (error: InfrastructureError | ValidationError): string => {
  if (error instanceof ValidationError) return error.message;
  const { publicMessage } = error;
  if (publicMessage === undefined) return NETWORK_LOGIN_MESSAGE;
  return publicMessage;
};

export const firstOrganizationId = (
  ids: readonly OrganizationId[],
): Result<OrganizationId, NotFoundError> => {
  const [first] = ids;
  if (first === undefined) {
    return Result.err(new NotFoundError({ resource: 'organization', id: 'first' }));
  }
  return Result.ok(first);
};

const authTransport = (cause: unknown): InfrastructureError => {
  const error = cause instanceof Error ? cause : new Error('operator auth request failed');
  return new InfrastructureError({
    system: 'http',
    message: error.message,
    cause: error,
    publicMessage: 'Não foi possível completar a autenticação do operador.',
  });
};

const credentialsRejected = (message: string): ValidationError =>
  new ValidationError({ field: 'credentials', message });

const fetchAuthText = (
  path: `/${string}`,
  init: RequestInit,
  operatorHttp: OperatorHttp,
): Promise<
  Result<
    { readonly ok: boolean; readonly status: number; readonly text: string },
    InfrastructureError
  >
> =>
  Result.tryPromise(
    {
      try: async (context) => {
        const response = await operatorHttp.fetcher(powerAdminApiPath(path), {
          ...init,
          signal: boundFetcherSignal(operatorHttp, context),
        });
        return { ok: response.ok, status: response.status, text: await response.text() };
      },
      catch: authTransport,
    },
    { signal: operatorHttp.signal },
  );

const messageFromAuthBody = (text: string, fallback: string): string =>
  parseJson(text, AuthMessageBody).match({
    ok: (body) => body.message,
    err: () => fallback,
  });

const GENERIC_AUTH_FAILURE = 'AUTH_FAILED';

const codeFromAuthBody = (text: string, fallback: string): string =>
  parseJson(text, AuthFailureBody).match({
    ok: (body) => body.code ?? fallback,
    err: () => fallback,
  });

export const InitialPasswordChange = z.strictObject({
  email: z.string().min(1),
  currentPassword: z.string().min(1),
  newPassword: z.string().min(1),
});
export type InitialPasswordChange = z.infer<typeof InitialPasswordChange>;

export const SignInOutcome = z.enum(['signed-in', 'password-change-required']);
export type SignInOutcome = z.infer<typeof SignInOutcome>;

export const signInOperator = (
  credentials: OperatorCredentials,
  operatorHttp: OperatorHttp,
): Promise<Result<SignInOutcome, InfrastructureError | ValidationError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      fetchAuthText(
        '/api/auth/sign-in/email',
        {
          method: 'POST',
          credentials: 'include',
          headers: jsonHeaders,
          body: JSON.stringify(credentials),
        },
        operatorHttp,
      ),
    );
    if (!fetched.ok) {
      const failure = codeFromAuthBody(fetched.text, GENERIC_AUTH_FAILURE);
      if (failure === 'PASSWORD_CHANGE_REQUIRED') {
        return Result.ok(SignInOutcome.enum['password-change-required']);
      }
      return Result.err(
        credentialsRejected(
          messageFromAuthBody(fetched.text, 'Não foi possível entrar. Verifique as credenciais.'),
        ),
      );
    }
    const signedIn = yield* parseJson(fetched.text, SignInResponse);
    if (signedIn.twoFactorRedirect === true) {
      return Result.err(
        credentialsRejected(
          'Login de desenvolvimento indisponível: peça a um administrador para criar o operador.',
        ),
      );
    }
    return Result.ok(SignInOutcome.enum['signed-in']);
  });

export const changeInitialPassword = (
  input: InitialPasswordChange,
  operatorHttp: OperatorHttp,
): Promise<Result<void, InfrastructureError | ValidationError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      fetchAuthText(
        '/api/auth/initial-password/change',
        {
          method: 'POST',
          credentials: 'include',
          headers: jsonHeaders,
          body: JSON.stringify(input),
        },
        operatorHttp,
      ),
    );
    if (!fetched.ok) {
      return Result.err(
        credentialsRejected(messageFromAuthBody(fetched.text, 'Não foi possível trocar a senha.')),
      );
    }
    return Result.ok();
  });

// A refused sign-out leaves the session live on Power IAM and its HttpOnly cookie in the browser,
// so only a 2xx answer counts as signed out.
export const signOutOperator = (
  operatorHttp: OperatorHttp,
): Promise<Result<void, InfrastructureError | ValidationError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      fetchAuthText(
        '/api/auth/sign-out',
        { method: 'POST', credentials: 'include', headers: jsonHeaders, body: '{}' },
        operatorHttp,
      ),
    );
    if (!fetched.ok) {
      return Result.err(
        new ValidationError({
          field: 'session',
          message: messageFromAuthBody(fetched.text, 'Power IAM recusou o encerramento da sessão.'),
        }),
      );
    }
    return Result.ok();
  });

export const listOrganizationIds = (
  operatorHttp: OperatorHttp,
): Promise<Result<readonly OrganizationId[], InfrastructureError | ValidationError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      fetchAuthText(
        '/api/auth/organization/list',
        { credentials: 'include', headers: { accept: 'application/json' } },
        operatorHttp,
      ),
    );
    if (!fetched.ok) {
      return Result.err(authTransport(new Error(`organization list HTTP ${fetched.status}`)));
    }
    return organizationIdsFromText(fetched.text);
  });

export const setActiveOrganization = (
  organizationId: OrganizationId,
  operatorHttp: OperatorHttp,
): Promise<Result<void, InfrastructureError>> =>
  Result.gen(async function* () {
    yield* Result.await(
      fetchAuthText(
        '/api/auth/organization/set-active',
        {
          method: 'POST',
          credentials: 'include',
          headers: jsonHeaders,
          body: JSON.stringify({ organizationId }),
        },
        operatorHttp,
      ),
    );
    return Result.ok();
  });

export const fetchOperatorEmail = (
  operatorHttp: OperatorHttp,
): Promise<Result<string, InfrastructureError | UnauthorizedError | ValidationError>> =>
  Result.gen(async function* () {
    const fetched = yield* Result.await(
      fetchAuthText(
        '/api/auth/get-session',
        { credentials: 'include', headers: { accept: 'application/json' } },
        operatorHttp,
      ),
    );
    if (!fetched.ok) {
      return Result.err(
        new UnauthorizedError({
          reason: 'missing_token',
          message: 'Sessão do operador não autenticada.',
        }),
      );
    }
    const session = yield* parseJson(fetched.text, OperatorSessionEmail);
    return Result.ok(session.user.email);
  });
